import React, { useState, useMemo, useEffect } from 'react';
import { Download, Search, FileText, Printer } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getHistory } from '../../utils/api';

export default function History() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fw, setFw] = useState('all');
  const [outcome, setOutcome] = useState('all');

  useEffect(() => {
    getHistory()
      .then(setChecks)
      .catch(() => toast.error('Failed to load check history.'))
      .finally(() => setLoading(false));
  }, []);

  const frameworks = ['all', ...Array.from(new Set(checks.map((c) => c.framework_code)))];

  const rows = useMemo(() => {
    return checks.filter((c) => {
      const matchSearch = (c.entity_name || '').toLowerCase().includes(search.toLowerCase());
      const matchFw = fw === 'all' || c.framework_code === fw;
      const matchOut = outcome === 'all' ||
        (outcome === 'compliant' && c.compliant) ||
        (outcome === 'non-compliant' && !c.compliant);
      return matchSearch && matchFw && matchOut;
    });
  }, [checks, search, fw, outcome]);

  const exportCSV = () => {
    const header = ['ID', 'Date', 'Entity', 'Framework', 'Score', 'Risk', 'Compliant'];
    const lines = [header.join(',')].concat(
      rows.map((r) => [
        r.check_id,
        `"${new Date(r.created_at).toLocaleString()}"`,
        `"${r.entity_name}"`,
        r.framework_code,
        r.overall_score + '%',
        r.risk_level,
        r.compliant ? 'Yes' : 'No'
      ].join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `esgine-check-history-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Check history exported to CSV.');
  };

  const gapCount = (c) => (c.results_json || []).filter(r => r.outcome === 'fail').length;

  return (
    <div className="max-w-[1280px]">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">Audit trail</div>
          <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
            Check <span className="italic">history.</span>
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
            All compliance checks for your account. Export for audit trail.
          </p>
        </div>
        <Button
          onClick={exportCSV}
          className="bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-11 px-5 text-[13px] inline-flex items-center gap-2"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-[#E8E5DC] bg-white p-4 mb-5 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0B1F2D]/45" />
          <Input
            placeholder="Search by entity name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-10 bg-[#FAFAF5] border-[#E8E5DC] focus-visible:ring-[#1B6F5E]"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={fw} onValueChange={setFw}>
            <SelectTrigger className="w-[180px] h-11 bg-[#FAFAF5] border-[#E8E5DC] focus:ring-[#1B6F5E]">
              <SelectValue placeholder="Framework" />
            </SelectTrigger>
            <SelectContent>
              {frameworks.map((f) => (
                <SelectItem key={f} value={f}>{f === 'all' ? 'All frameworks' : f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger className="w-[180px] h-11 bg-[#FAFAF5] border-[#E8E5DC] focus:ring-[#1B6F5E]">
              <SelectValue placeholder="Outcome" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="compliant">Compliant</SelectItem>
              <SelectItem value="non-compliant">Non-Compliant</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-[#E8E5DC] bg-white overflow-hidden">
        {loading ? (
          <div className="px-5 py-16 text-center text-[13px] text-[#0B1F2D]/55">Loading check history...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#FAFAF5]/70 border-b border-[#E8E5DC]">
                <tr className="text-[10.5px] tracking-[0.14em] uppercase text-[#0B1F2D]/55">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Entity</th>
                  <th className="px-5 py-3 font-medium">Framework</th>
                  <th className="px-5 py-3 font-medium">Outcome</th>
                  <th className="px-5 py-3 font-medium">Score</th>
                  <th className="px-5 py-3 font-medium">Risk</th>
                  <th className="px-5 py-3 font-medium">Gaps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E5DC]">
                {rows.map((r) => (
                  <tr key={r.check_id} className="hover:bg-[#FAFAF5]/60 transition-colors">
                    <td className="px-5 py-4 text-[12.5px] text-[#0B1F2D]/75 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-[#0B1F2D] font-medium">
                      <div className="inline-flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-[#0B1F2D]/50" />
                        {r.entity_name}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-mono-tight text-[11px] px-2 py-1 rounded bg-[#0B1F2D]/[0.04] border border-[#E8E5DC] text-[#0B1F2D]/80">
                        {r.framework_code}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 text-[10.5px] font-medium tracking-[0.06em] px-2 py-1 rounded-full border ${
                        r.compliant
                          ? 'bg-[#E8F3EE] text-[#1B6F5E] border-[#1B6F5E]/25'
                          : 'bg-[#FBEAEA] text-[#A4243B] border-[#A4243B]/25'
                      }`}>
                        {r.compliant ? '✓ Compliant' : '✗ Non-Compliant'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`font-mono-tight text-[13px] font-semibold ${
                        parseFloat(r.overall_score) >= 80 ? 'text-[#1B6F5E]' :
                        parseFloat(r.overall_score) >= 70 ? 'text-[#0B1F2D]' : 'text-[#A4243B]'
                      }`}>
                        {parseFloat(r.overall_score).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-medium tracking-[0.06em] uppercase ${
                        r.risk_level === 'LOW' ? 'text-[#0B1F2D]/70' : 'text-[#A4243B]'
                      }`}>
                        {r.risk_level}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`font-mono-tight text-[13px] font-semibold ${
                        gapCount(r) === 0 ? 'text-[#1B6F5E]' : 'text-[#A4243B]'
                      }`}>
                        {gapCount(r)}
                      </span>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-[13px] text-[#0B1F2D]/55">
                      {checks.length === 0 ? 'No compliance checks found. Run your first check to see results here.' : 'No checks match your filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-[12px] text-[#0B1F2D]/55">
        <span>Showing {rows.length} of {checks.length} checks</span>
        <span className="font-mono-tight">/account</span>
      </div>
    </div>
  );
}
